/* ============================================================
   MA Cart Drawer JS
   - Remove All with inline confirm
   - Rewards bar client-side updates (piecewise fill, station pills, message)
   - Gift auto-add / auto-remove on cart updates
   - Quick add single-variant handler
   ============================================================ */

(function () {
  'use strict';

  // ── Helpers ────────────────────────────────────────────────

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
    };
  }

  function moneyFormat(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ── Rewards bar updater ─────────────────────────────────────

  const SVG_TRUCK = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h11v9H3z"/><path d="M14 9h4l3 3v3h-7"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>`;
  const SVG_GIFT = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10h16v10H4z"/><path d="M12 10v10"/><path d="M3 7h18v3H3z"/><path d="M12 7c-2.5-3.5-6-2-5 0 .5 1 2.5 1 5 0z"/><path d="M12 7c2.5-3.5 6-2 5 0-.5 1-2.5 1-5 0z"/></svg>`;
  const SVG_CHECK = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l4 4 10-10"/></svg>`;
  const SVG_SPARKLE = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6z"/><path d="M18.5 14l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z"/></svg>`;

  function cartHasSubscription(cartItems) {
    return Array.isArray(cartItems) && cartItems.some((item) => item.selling_plan_allocation);
  }

  // Returns the cart subtotal (sum of line item prices after item-level discounts).
  // This matches what Shopify Functions receive as cart.cost.subtotalAmount.
  function cartEffectiveTotal(cart) {
    return cart.items_subtotal_price ?? cart.total_price;
  }

  function updateRewardsBar(cartTotalCents, hasSubscription = false) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const shippingEnabled = drawer.dataset.shippingEnabled === 'true';
    const shippingThreshold = parseInt(drawer.dataset.shippingThreshold || '0', 10);

    // Build ordered milestone list (shipping first, then gift1, gift2)
    const milestones = [
      {
        key: 'shipping',
        enabled: shippingEnabled,
        threshold: shippingThreshold,
        msgProgress: drawer.dataset.shippingMsgProgress || '',
        msgUnlocked: drawer.dataset.shippingMsgUnlocked || '',
      },
      {
        key: 'gift-1',
        enabled: drawer.dataset.gift1Enabled === 'true',
        threshold: parseInt(drawer.dataset.gift1Threshold || '0', 10),
        msgProgress: drawer.dataset.gift1MsgProgress || '',
        msgUnlocked: drawer.dataset.gift1MsgUnlocked || '',
      },
      {
        key: 'gift-2',
        enabled: drawer.dataset.gift2Enabled === 'true',
        threshold: parseInt(drawer.dataset.gift2Threshold || '0', 10),
        msgProgress: drawer.dataset.gift2MsgProgress || '',
        msgUnlocked: drawer.dataset.gift2MsgUnlocked || '',
      },
    ].filter((m) => m.enabled);

    if (milestones.length === 0) return;

    // Subscription auto-unlocks shipping
    const effectiveTotal =
      hasSubscription && shippingEnabled && cartTotalCents < shippingThreshold
        ? shippingThreshold
        : cartTotalCents;

    const maxThreshold = parseInt(drawer.dataset.maxThreshold || '0', 10);

    // Fill is a straight ratio of cart total to the highest threshold
    const fillPct = maxThreshold > 0
      ? Math.min(100, Math.max(0, (effectiveTotal / maxThreshold) * 100))
      : 0;

    const fill = drawer.querySelector('.ma-rewards-bar__fill');
    if (fill) fill.style.width = fillPct.toFixed(2) + '%';

    // Update station pills
    milestones.forEach((m) => {
      const unlocked =
        m.key === 'shipping'
          ? hasSubscription || cartTotalCents >= m.threshold
          : cartTotalCents >= m.threshold;
      const subUnlocked = m.key === 'shipping' && hasSubscription && cartTotalCents < m.threshold;

      const pill = drawer.querySelector(`.ma-rewards-bar__station-pill[data-milestone="${m.key}"]`);
      if (!pill) return;

      pill.classList.toggle('ma-rewards-bar__station-pill--unlocked', unlocked);
      pill.classList.toggle('ma-rewards-bar__station-pill--sub', subUnlocked && !unlocked);

      const iconEl = pill.querySelector('.ma-rewards-bar__station-icon');
      if (iconEl) {
        if (unlocked) {
          iconEl.innerHTML = SVG_CHECK;
        } else {
          iconEl.innerHTML = m.key === 'shipping' ? SVG_TRUCK : SVG_GIFT;
        }
      }
    });

    // Subscription perk strip
    const subPerk = drawer.querySelector('.ma-rewards-bar__sub-perk');
    if (subPerk) {
      subPerk.hidden = !(hasSubscription && shippingEnabled);
    }

    // Status message
    const statusEl = drawer.querySelector('.ma-rewards-bar__status');
    if (!statusEl) return;

    let msg = '';
    for (const m of milestones) {
      const unlocked =
        m.key === 'shipping'
          ? hasSubscription || cartTotalCents >= m.threshold
          : cartTotalCents >= m.threshold;
      if (!unlocked) {
        const remaining = m.threshold - cartTotalCents;
        msg = m.msgProgress.replace('[amount]', moneyFormat(remaining));
        break;
      }
    }

    const allUnlocked = !msg;
    statusEl.classList.toggle('ma-rewards-bar__status--unlocked', allUnlocked);

    if (allUnlocked) {
      const celebrateSpan = statusEl.querySelector('.ma-rewards-bar__celebrate-icon');
      if (!celebrateSpan) {
        statusEl.innerHTML = `<span class="ma-rewards-bar__celebrate-icon">${SVG_SPARKLE}</span>You've unlocked everything.`;
      } else {
        const textNode = celebrateSpan.nextSibling;
        if (textNode) textNode.textContent = "You've unlocked everything.";
      }
    } else {
      statusEl.textContent = msg;
    }
  }

  // ── Gift auto-add / auto-remove ─────────────────────────────

  let giftUpdateInProgress = false;
  let giftUpdateQueued = false;

  async function handleGiftMilestones(cartData) {
    // If already running, mark that another run is needed but don't double-add.
    // This guards the race where two refreshes fire in quick succession (e.g. from
    // the wizard: PUB_SUB_EVENTS.cartUpdate + the MutationObserver when the drawer opens).
    if (giftUpdateInProgress) {
      giftUpdateQueued = true;
      return;
    }
    giftUpdateInProgress = true;
    giftUpdateQueued = false;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const gifts = [
      {
        enabled: drawer.dataset.gift1Enabled === 'true',
        threshold: parseInt(drawer.dataset.gift1Threshold || '0', 10),
        variantId: drawer.dataset.gift1VariantId,
        price: parseInt(drawer.dataset.gift1Price || '0', 10),
      },
      {
        enabled: drawer.dataset.gift2Enabled === 'true',
        threshold: parseInt(drawer.dataset.gift2Threshold || '0', 10),
        variantId: drawer.dataset.gift2VariantId,
        price: parseInt(drawer.dataset.gift2Price || '0', 10),
      },
    ].filter((g) => g.enabled && g.variantId);

    if (gifts.length === 0) {
      giftUpdateInProgress = false;
      return;
    }

    const cartTotal = cartEffectiveTotal(cartData);
    const cartItems = cartData.items || [];
    let changed = false;

    try {
      for (const gift of gifts) {
        // Only look for the auto-gift instance — a customer-purchased copy of the same
        // product must not block the free gift from being added.
        const autoGiftItem = cartItems.find(
          (item) =>
            String(item.variant_id) === String(gift.variantId) &&
            item.properties &&
            item.properties._ma_auto_gift === 'true'
        );
        const autoGiftInCart = !!autoGiftItem;

        // Circular-dependency guard: if the gift product is already in the cart as a
        // paid item, making one unit free would drop the effective subtotal. If that
        // would push it below the threshold, don't auto-add (it would just oscillate).
        const paidGiftInCart = cartItems.some(
          (item) =>
            String(item.variant_id) === String(gift.variantId) &&
            !(item.properties && item.properties._ma_auto_gift === 'true') &&
            item.final_price > 0
        );
        // Any copy at final_price === 0 means items_subtotal already reflects free pricing.
        // This covers both Cart Transform free items and auto-gift items made free.
        const alreadyFreeInCart = cartItems.some(
          (item) =>
            String(item.variant_id) === String(gift.variantId) &&
            item.final_price === 0
        );
        const wouldCreateLoop =
          paidGiftInCart &&
          !alreadyFreeInCart &&
          gift.price > 0 &&
          cartTotal - gift.price < gift.threshold;

        const shouldBeInCart = cartTotal >= gift.threshold && !wouldCreateLoop;

        if (shouldBeInCart && !autoGiftInCart) {
          const resp = await fetch(window.routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({ id: gift.variantId, quantity: 1, properties: { _ma_auto_gift: 'true' } }),
          });
          if (resp.ok) changed = true;
          else {
            const body = await resp.text();
            console.error('MA Gift: add failed', resp.status, body);
          }
        } else if (!shouldBeInCart && autoGiftInCart) {
          // Remove only the auto-gift line item by its key, leaving any paid copy untouched.
          const resp = await fetch(window.routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify({ id: autoGiftItem.key, quantity: 0 }),
          });
          if (resp.ok) changed = true;
          else console.error('MA Gift: remove failed', resp.status);
        }
      }
    } catch (e) {
      console.error('MA Gift: milestone error', e);
    } finally {
      giftUpdateInProgress = false;
    }

    if (changed || giftUpdateQueued) {
      giftUpdateQueued = false;
      await refreshCartDrawerSection();
    }
  }

  // ── Nav badge update ─────────────────────────────────────────

  function dispatchCartUpdate(count) {
    document.dispatchEvent(new CustomEvent('ma:cart.updated', { detail: { item_count: count } }));
  }

  // ── Section refresh ─────────────────────────────────────────

  async function refreshCartDrawerSection() {
    try {
      const resp = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      for (const selector of ['cart-drawer-items', '.cart-drawer__footer', '.ma-cart__quick-add']) {
        const target = document.querySelector(selector);
        const source = doc.querySelector(selector);
        if (target && source) target.replaceWith(source);
      }

      // Always replace .ma-rewards-bar from server HTML — Liquid cart.total_price includes
      // discount codes that the AJAX /cart.js endpoint does not reflect.
      // .ma-cart__empty is also outside cart-drawer-items so handle it the same way.
      for (const selector of ['.ma-rewards-bar', '.ma-cart__empty']) {
        const target = document.querySelector(selector);
        const source = doc.querySelector(selector);
        if (target && source) {
          target.replaceWith(source);
        } else if (target && !source) {
          target.remove();
        } else if (!target && source) {
          document.querySelector('cart-drawer-items')?.before(source);
        }
      }

      // Sync remove-all button (lives in the header, before the close button)
      const targetRemoveAll = document.querySelector('.ma-cart__remove-all-wrapper');
      const sourceRemoveAll = doc.querySelector('.ma-cart__remove-all-wrapper');
      if (targetRemoveAll && sourceRemoveAll) {
        targetRemoveAll.replaceWith(sourceRemoveAll);
      } else if (targetRemoveAll && !sourceRemoveAll) {
        targetRemoveAll.remove();
      } else if (!targetRemoveAll && sourceRemoveAll) {
        document.querySelector('.ma-cart__close-btn')?.before(sourceRemoveAll);
      }

      // Sync is-empty on cart-drawer (controls footer visibility)
      const cartDrawerEl = document.querySelector('cart-drawer');
      const newCartDrawerEl = doc.querySelector('cart-drawer');
      if (cartDrawerEl && newCartDrawerEl) {
        cartDrawerEl.classList.toggle('is-empty', newCartDrawerEl.classList.contains('is-empty'));
      }

      const cartResp = await fetch('/cart.js');
      const cart = await cartResp.json();
      dispatchCartUpdate(cart.item_count);
      await handleGiftMilestones(cart);
    } catch (e) {
      console.error('MA Cart: section refresh failed', e);
    }
  }

  async function fullCartDrawerRefresh() {
    try {
      const resp = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newCartDrawer = doc.querySelector('#CartDrawer');
      const currentCartDrawer = document.querySelector('#CartDrawer');
      if (newCartDrawer && currentCartDrawer) {
        currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
        const overlay = document.getElementById('CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', () => {
            const cd = document.querySelector('cart-drawer');
            if (cd) cd.close();
          });
        }
      }

      const cartResp = await fetch('/cart.js');
      const cart = await cartResp.json();
      dispatchCartUpdate(cart.item_count);
    } catch (e) {
      console.error('MA Cart: full refresh failed', e);
    }
  }

  // ── Remove All ──────────────────────────────────────────────

  function initRemoveAll() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.ma-cart__remove-all-btn')) {
        const wrapper = e.target.closest('.ma-cart__remove-all-wrapper');
        if (!wrapper) return;
        wrapper.querySelector('.ma-cart__remove-all-btn').hidden = true;
        const confirm = wrapper.querySelector('.ma-cart__remove-all-confirm');
        if (confirm) confirm.hidden = false;
        return;
      }

      if (e.target.matches('.ma-cart__remove-all-cancel')) {
        const wrapper = e.target.closest('.ma-cart__remove-all-wrapper');
        if (!wrapper) return;
        wrapper.querySelector('.ma-cart__remove-all-btn').hidden = false;
        const confirm = wrapper.querySelector('.ma-cart__remove-all-confirm');
        if (confirm) confirm.hidden = true;
        return;
      }

      if (e.target.matches('.ma-cart__remove-all-yes')) {
        e.target.disabled = true;
        e.target.textContent = '...';
        fetch('/cart/clear.js', { method: 'POST' })
          .then(() => fullCartDrawerRefresh())
          .catch((err) => console.error('MA Cart: clear failed', err));
        return;
      }
    });
  }

  // ── Quick add ───────────────────────────────────────────────

  const SVG_PLUS_SM = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
  const SVG_PLUS_SM_WHITE = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
  const SVG_SPINNER_SM = '<span style="width:1.2rem;height:1.2rem;border:1.5px solid currentColor;border-top-color:transparent;border-radius:50%;display:inline-block;animation:ma-spin 0.6s linear infinite"></span>';

  function addToCart(variantId, btn, onSuccess) {
    btn.disabled = true;
    btn.innerHTML = SVG_SPINNER_SM;
    fetch(window.routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({ id: variantId, quantity: 1 }),
    })
      .then((r) => r.json())
      .then(() => {
        if (onSuccess) onSuccess();
        else refreshCartDrawerSection();
      })
      .catch((err) => {
        console.error('MA Cart: quick add failed', err);
        btn.disabled = false;
      });
  }

  function initQuickAdd() {
    // Desktop YML panel — simple add button
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ma-cart__quick-add-btn');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      addToCart(variantId, btn, () => {
        btn.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l4 4 10-10"/></svg>';
        refreshCartDrawerSection();
      });
    });

    // Mobile YML card — simple add button
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.ma-cart__yml-card-add');
      if (!btn) return;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      const origHtml = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span style="width:1.1rem;height:1.1rem;border:1.5px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;display:inline-block;animation:ma-spin 0.6s linear infinite"></span>';
      fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((r) => r.json())
        .then(() => {
          const card = btn.closest('.ma-cart__yml-card');
          if (card) card.remove();
          refreshCartDrawerSection();
        })
        .catch((err) => {
          console.error('MA Cart: quick add failed', err);
          btn.disabled = false;
          btn.innerHTML = origHtml;
        });
    });
  }

  // ── Variant picker ───────────────────────────────────────────

  function isMobile() {
    return window.innerWidth <= 989;
  }

  function buildChipGroups(options, variants, selections) {
    return options.map((opt) => {
      const selected = selections[opt.name] || null;

      const chipsHtml = opt.values.map((val) => {
        // Check availability: find variant that matches this value + all currently-selected other groups
        const soldOut = !variants.some((v) => {
          if (!v.available) return false;
          const thisOpt = v.options[options.indexOf(opt)];
          if (thisOpt !== val) return false;
          // check other selected groups still match
          return options.every((o, oi) => {
            if (o.name === opt.name) return true;
            const sel = selections[o.name];
            return !sel || v.options[oi] === sel;
          });
        });

        const cls = [
          'ma-cart__vp-chip',
          val === selected ? 'is-selected' : '',
          soldOut ? 'is-sold-out' : '',
        ].filter(Boolean).join(' ');

        return `<button class="${cls}" data-group="${opt.name}" data-value="${val}" ${soldOut ? 'disabled' : ''}>${val}</button>`;
      }).join('');

      const selLabel = selected
        ? `<span class="ma-cart__vp-group-selected has-value">${selected}</span>`
        : `<span class="ma-cart__vp-group-selected"><em>Choose one</em></span>`;

      return `
        <div class="ma-cart__vp-group" data-group-name="${opt.name}">
          <div class="ma-cart__vp-group-header">
            <div class="ma-cart__vp-group-name">${opt.name.toUpperCase()}</div>
            ${selLabel}
          </div>
          <div class="ma-cart__vp-chips">${chipsHtml}</div>
        </div>`;
    }).join('');
  }

  function findVariantForSelections(variants, options, selections) {
    return variants.find((v) =>
      options.every((opt, oi) => v.options[oi] === selections[opt.name])
    ) || null;
  }

  // ── Mobile variant picker (sheet-within-sheet) ──────────────

  let vpState = { options: [], variants: [], selections: {}, container: null };

  function openMobileVariantPicker(btn) {
    const content = document.querySelector('.ma-cart__content.drawer__inner');
    if (!content) return;

    const sheet = content.querySelector('.ma-cart__vp-sheet');
    const overlay = content.querySelector('.ma-cart__vp-overlay');
    if (!sheet || !overlay) return;

    const options = JSON.parse(btn.dataset.options || '[]');
    const variants = JSON.parse(btn.dataset.variants || '[]');
    vpState = { options, variants, selections: {}, container: sheet };

    // Populate header
    sheet.querySelector('.ma-cart__vp-title').textContent = btn.dataset.title || '';
    sheet.querySelector('.ma-cart__vp-price').innerHTML = btn.dataset.price || '';
    const img = sheet.querySelector('.ma-cart__vp-img');
    img.src = btn.dataset.image || '';
    img.alt = btn.dataset.title || '';

    // Render chip groups
    sheet.querySelector('.ma-cart__vp-groups').innerHTML = buildChipGroups(options, variants, {});
    sheet.querySelector('.ma-cart__vp-add').disabled = true;
    sheet.querySelector('.ma-cart__vp-add').textContent = 'Choose options to add';

    sheet.hidden = false;
    overlay.classList.add('is-open');
  }

  function closeMobileVariantPicker() {
    const content = document.querySelector('.ma-cart__content.drawer__inner');
    if (!content) return;
    content.querySelector('.ma-cart__vp-sheet').hidden = true;
    content.querySelector('.ma-cart__vp-overlay').classList.remove('is-open');
    vpState = { options: [], variants: [], selections: {}, container: null };
  }

  // ── Desktop variant picker (inline expand in YML panel) ─────

  function openDesktopVariantPicker(btn) {
    const item = btn.closest('.ma-cart__quick-add-item');
    if (!item) return;

    // Collapse any other expanded item first
    document.querySelectorAll('.ma-cart__quick-add-item.is-expanded').forEach((el) => {
      if (el !== item) collapseDesktopItem(el);
    });

    if (item.classList.contains('is-expanded')) {
      collapseDesktopItem(item);
      return;
    }

    const options = JSON.parse(btn.dataset.options || '[]');
    const variants = JSON.parse(btn.dataset.variants || '[]');
    const selections = {};
    const state = { options, variants, selections };

    // Wrap original row content
    const origContents = Array.from(item.childNodes);
    const rowEl = document.createElement('div');
    rowEl.className = 'ma-cart__quick-add-item-row';
    origContents.forEach((n) => rowEl.appendChild(n));
    item.appendChild(rowEl);

    // Expand panel
    const expandEl = document.createElement('div');
    expandEl.className = 'ma-cart__quick-add-item-expand';
    expandEl.innerHTML = buildChipGroups(options, variants, {}) +
      `<button class="ma-cart__vp-add" disabled>Choose options to add</button>`;
    item.appendChild(expandEl);
    item.classList.add('is-expanded');
    item._vpState = state;
    btn.classList.add('is-active');
  }

  function collapseDesktopItem(item) {
    const rowEl = item.querySelector('.ma-cart__quick-add-item-row');
    if (rowEl) {
      const children = Array.from(rowEl.childNodes);
      children.forEach((n) => item.insertBefore(n, rowEl));
      rowEl.remove();
    }
    item.querySelector('.ma-cart__quick-add-item-expand')?.remove();
    item.classList.remove('is-expanded');
    item._vpState = null;
    item.querySelector('.ma-cart__quick-add-options-btn')?.classList.remove('is-active');
  }

  function initVariantPicker() {
    // Open picker on chevron click
    document.addEventListener('click', (e) => {
      // Mobile YML card options button
      const mobileBtn = e.target.closest('.ma-cart__yml-card-options');
      if (mobileBtn) {
        openMobileVariantPicker(mobileBtn);
        return;
      }

      // Desktop YML panel options button
      const desktopBtn = e.target.closest('.ma-cart__quick-add-options-btn');
      if (desktopBtn) {
        if (isMobile()) {
          openMobileVariantPicker(desktopBtn);
        } else {
          openDesktopVariantPicker(desktopBtn);
        }
        return;
      }

      // Close mobile picker via overlay or close button
      if (e.target.closest('.ma-cart__vp-overlay') || e.target.closest('.ma-cart__vp-close')) {
        closeMobileVariantPicker();
        return;
      }
    });

    // Chip selection — mobile sheet
    document.addEventListener('click', (e) => {
      const chip = e.target.closest('.ma-cart__vp-sheet .ma-cart__vp-chip');
      if (!chip || chip.disabled) return;

      const group = chip.dataset.group;
      const val = chip.dataset.value;
      vpState.selections[group] = val;

      // Re-render chips
      const sheet = chip.closest('.ma-cart__vp-sheet');
      sheet.querySelector('.ma-cart__vp-groups').innerHTML = buildChipGroups(vpState.options, vpState.variants, vpState.selections);

      const allChosen = vpState.options.every((o) => vpState.selections[o.name]);
      const addBtn = sheet.querySelector('.ma-cart__vp-add');
      if (allChosen) {
        const variant = findVariantForSelections(vpState.variants, vpState.options, vpState.selections);
        addBtn.disabled = !variant?.available;
        addBtn.innerHTML = variant?.available
          ? `${SVG_PLUS_SM_WHITE} Add to cart · ${variant.price ? (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : ''}`
          : 'Sold out';
        addBtn.dataset.variantId = variant?.id || '';
      } else {
        addBtn.disabled = true;
        addBtn.textContent = 'Choose options to add';
      }
    });

    // Chip selection — desktop inline
    document.addEventListener('click', (e) => {
      const chip = e.target.closest('.ma-cart__quick-add-item-expand .ma-cart__vp-chip');
      if (!chip || chip.disabled) return;

      const item = chip.closest('.ma-cart__quick-add-item');
      if (!item?._vpState) return;
      const state = item._vpState;

      state.selections[chip.dataset.group] = chip.dataset.value;

      const expandEl = item.querySelector('.ma-cart__quick-add-item-expand');
      const addBtn = expandEl.querySelector('.ma-cart__vp-add');
      expandEl.innerHTML = buildChipGroups(state.options, state.variants, state.selections) +
        addBtn.outerHTML;

      const allChosen = state.options.every((o) => state.selections[o.name]);
      const newAddBtn = expandEl.querySelector('.ma-cart__vp-add');
      if (allChosen) {
        const variant = findVariantForSelections(state.variants, state.options, state.selections);
        newAddBtn.disabled = !variant?.available;
        newAddBtn.innerHTML = variant?.available
          ? `${SVG_PLUS_SM_WHITE} Add to cart · ${variant.price ? (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : ''}`
          : 'Sold out';
        newAddBtn.dataset.variantId = variant?.id || '';
      } else {
        newAddBtn.disabled = true;
        newAddBtn.textContent = 'Choose options to add';
      }

      // Re-attach state reference (innerHTML wipe removed it)
      item._vpState = state;
    });

    // Add to cart from picker (mobile sheet or desktop inline)
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('.ma-cart__vp-add:not(:disabled)');
      if (!addBtn) return;
      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;

      const origHtml = addBtn.innerHTML;
      addBtn.disabled = true;
      addBtn.innerHTML = SVG_SPINNER_SM;

      fetch(window.routes.cart_add_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      })
        .then((r) => r.json())
        .then(() => {
          closeMobileVariantPicker();
          // Collapse desktop inline picker if open
          document.querySelectorAll('.ma-cart__quick-add-item.is-expanded').forEach(collapseDesktopItem);
          refreshCartDrawerSection();
        })
        .catch((err) => {
          console.error('MA Cart: variant add failed', err);
          addBtn.disabled = false;
          addBtn.innerHTML = origHtml;
        });
    });
  }

  // ── PubSub subscription ─────────────────────────────────────

  function initCartUpdateSubscription() {
    if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      setTimeout(initCartUpdateSubscription, 500);
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
      refreshCartDrawerSection();
    });
  }

  // ── Body scroll lock ────────────────────────────────────────

  function initScrollLock() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    let wasActive = false;
    const observer = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      document.body.classList.toggle('ma-cart-open', isActive);
      if (isActive && !wasActive) refreshCartDrawerSection();
      wasActive = isActive;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // ── Spinner keyframe ─────────────────────────────────────────

  function injectSpinnerStyle() {
    if (document.getElementById('ma-cart-spin')) return;
    const style = document.createElement('style');
    style.id = 'ma-cart-spin';
    style.textContent = '@keyframes ma-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  // ── Checkout loading state ───────────────────────────────────

  function initCheckoutLoading() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('#CartDrawer-Checkout');
      if (!btn || btn.disabled) return;
      btn.classList.add('is-loading');
    });

    // Reset and resync when browser restores page from bfcache (back button)
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      document.querySelectorAll('#CartDrawer-Checkout.is-loading').forEach((btn) => {
        btn.classList.remove('is-loading');
      });
      refreshCartDrawerSection();
    });
  }

  // ── Quiz wizard cart drawer open ─────────────────────────────
  // Dispatched by ma-quiz-wizard.js when the "Open cart drawer" completion option is used.
  // Refreshes cart contents first so the drawer shows the items just added by the quiz.

  document.addEventListener('ma:quiz-cart-drawer-open', async () => {
    await fullCartDrawerRefresh();
    const drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });

  // ── Init ────────────────────────────────────────────────────

  function init() {
    injectSpinnerStyle();
    initRemoveAll();
    initQuickAdd();
    initVariantPicker();
    initCheckoutLoading();
    initCartUpdateSubscription();
    initScrollLock();
    refreshCartDrawerSection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
